import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
  Chip,
  Menu,
  MenuItem,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Sync as SyncIcon,
  UploadFile as UploadFileIcon,
  FileDownload as FileDownloadIcon,
  ArrowDropDown as ArrowDropDownIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import registryService from '../services/registryService';
import RegistryEntriesTable from './RegistryEntriesTable';
import RegistryImportDialog from './RegistryImportDialog';
import RegistryEntryHistoryDialog from './RegistryEntryHistoryDialog';
import { isSyncable, sourceTypeLabel } from '../utils/registrySourceConfig';
import { buildEntryQuery } from '../hooks/useRegistryEntries';
import { registryKeyField } from '../utils/registryBindings';
import { downloadFile, fileNameFromContentDisposition, readBlobError } from '../utils/fileUtils';
import { MONO_FONT } from '../constants/typography';

// Dialogo «Voci» di un'anagrafica: tabella delle voci con barra delle azioni (import, export,
// storico, nuova voce) e i dialoghi di import e storico. Lo stato delle voci (`entriesState`,
// risultato di useRegistryEntries) resta nella pagina, che lo ricarica anche dal dialogo della voce.
const RegistryEntriesDialog = ({
  open,
  registry,
  onClose,
  onExited,
  entriesState,
  canEditEntries,
  onNewEntry,
  onEditEntry,
  onDeleteEntry,
  onImported
}) => {
  // Import, storico ed export delle voci (barra delle azioni del dialogo «Voci»)
  const [importOpen, setImportOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null); // { registry, entry|null }
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  // Ultimo bersaglio dello storico: resta nel titolo durante la transizione di chiusura
  const lastHistoryTarget = useRef(null);
  if (historyTarget) lastHistoryTarget.current = historyTarget;
  // All'apertura del dialogo l'avviso d'export di una sessione precedente sparisce
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setExportError(null);
  }

  // Esporta le voci (con i filtri e l'ordine correnti, o tutte con `withFilters: false`) e le scarica come file.
  // In caso d'errore imposta l'avviso del dialogo e rifiuta con un Error dal messaggio leggibile.
  const handleExport = async (format, { withFilters = true } = {}) => {
    if (exporting) return;
    setExportAnchor(null);
    setExportError(null);
    setExporting(true);
    try {
      const params = withFilters ? buildEntryQuery({ filters: entriesState.appliedFilters, sort: entriesState.sort }) : {};
      const response = await registryService.exportEntries(registry.id, { format, params });
      downloadFile(
        response.data,
        fileNameFromContentDisposition(response.headers, `${registry.code}-voci.${format}`),
        response.headers?.['content-type'],
      );
    } catch (err) {
      const message = (await readBlobError(err)) || 'Non riesco a esportare le voci: riprova tra poco.';
      setExportError(message);
      // chi chiama dal dialogo di import (modale, davanti all'avviso del dialogo) mostra lo stesso messaggio
      throw new Error(message);
    } finally {
      setExporting(false);
    }
  };

  // Import concluso (il dialogo di import si è chiuso dopo la scrittura): la pagina ricarica e avvisa
  const handleImported = (result) => {
    onImported(result);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        TransitionProps={{ onExited }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box component="span">Voci — {registry?.name || registry?.code}</Box>
          {registry?.code && (
            <Chip
              size="small"
              variant="outlined"
              label={registry.code}
              sx={{ fontFamily: MONO_FONT, fontWeight: 500 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {/* Le voci di un'anagrafica esterna le scrive il sync: qui si leggono soltanto. */}
          {isSyncable(registry) && (
            <Alert severity="info" icon={<SyncIcon fontSize="inherit" />} sx={{ mt: 1, mb: 2 }}>
              {`Voci sincronizzate da ${sourceTypeLabel(registry.sourceType)}: si aggiornano con la sincronizzazione, non si modificano qui.`}
            </Alert>
          )}
          {/* Le scritture sulle voci chiedono registries.update, come l'endpoint delle voci; lo storico c'e' solo dove ci sono voci locali (hasLocalEntries). */}
          {(registry?.internal || registry?.hasLocalEntries) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1, mb: 2 }}>
              {canEditEntries && (
                // Senza campo chiave il backend risponde 400: la UI lo dice prima (span: un bottone disabilitato non mostra il tooltip)
                <Tooltip title={registryKeyField(registry) ? '' : 'Serve un campo chiave nello schema'}>
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<UploadFileIcon />}
                      disabled={!registryKeyField(registry)}
                      onClick={() => setImportOpen(true)}
                    >
                      Importa
                    </Button>
                  </span>
                </Tooltip>
              )}
              {registry.hasLocalEntries && (
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  endIcon={<ArrowDropDownIcon />}
                  disabled={exporting}
                  id="registry-export-button"
                  aria-haspopup="menu"
                  aria-controls={exportAnchor ? 'registry-export-menu' : undefined}
                  aria-expanded={exportAnchor ? 'true' : undefined}
                  aria-busy={exporting}
                  onClick={(e) => setExportAnchor(e.currentTarget)}
                >
                  Esporta
                </Button>
              )}
              {registry.hasLocalEntries && (
                <Button
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => setHistoryTarget({ registry, entry: null })}
                >
                  Storico modifiche
                </Button>
              )}
              {canEditEntries && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={onNewEntry} sx={{ ml: 'auto' }}>
                  Nuova voce
                </Button>
              )}
            </Box>
          )}
          <Menu
            id="registry-export-menu"
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
            MenuListProps={{ 'aria-labelledby': 'registry-export-button' }}
          >
            <MenuItem onClick={() => handleExport('xlsx').catch(() => {})}>Excel</MenuItem>
            <MenuItem onClick={() => handleExport('csv').catch(() => {})}>CSV</MenuItem>
          </Menu>
          {exportError && (
            <Alert severity="error" onClose={() => setExportError(null)} sx={{ mb: 2 }}>{exportError}</Alert>
          )}

          <RegistryEntriesTable
            schema={registry?.schema}
            entries={entriesState.entries}
            totalElements={entriesState.totalElements}
            loading={entriesState.loading}
            error={entriesState.error}
            filters={entriesState.filters}
            onFilterChange={entriesState.setFilter}
            hasActiveFilters={entriesState.hasActiveFilters}
            onClearFilters={entriesState.clearFilters}
            sort={entriesState.sort}
            onSortChange={entriesState.toggleSort}
            page={entriesState.page}
            rowsPerPage={entriesState.rowsPerPage}
            onPageChange={entriesState.setPage}
            onRowsPerPageChange={entriesState.setRowsPerPage}
            canEdit={canEditEntries}
            onEdit={onEditEntry}
            onDelete={(entry) => onDeleteEntry(entry)}
            onHistory={registry?.hasLocalEntries
              ? (entry) => setHistoryTarget({ registry, entry })
              : undefined}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <RegistryImportDialog
        open={importOpen}
        registry={registry}
        onClose={() => setImportOpen(false)}
        onImported={handleImported}
        onDownloadTemplate={() => handleExport('xlsx', { withFilters: false })}
      />

      <RegistryEntryHistoryDialog
        open={Boolean(historyTarget)}
        registry={lastHistoryTarget.current?.registry}
        entry={lastHistoryTarget.current?.entry}
        onClose={() => setHistoryTarget(null)}
      />
    </>
  );
};

export default RegistryEntriesDialog;
